import {Nullable} from '../../util/type-helper';

export class SortHelper {
  static compareAscEmptyFirst<T extends number | string>(a: Nullable<T>, b: Nullable<T>): number {
    if (a === b || ((a === null || a === undefined) && (b === null || b === undefined))) {
      return 0;
    } else {
      if (a === null || a === undefined) {
        return -1;
      }
      if (b === null || b === undefined) {
        return 1;
      }
      return a > b ? 1 : -1;
    }
  }

  static comparatorDescEmptyFirst<S, T extends number | string>(attribute: (a: S) => Nullable<T>): (a: S, b: S) => number {
    return (a, b) => SortHelper.compareDescEmptyFirst(attribute(a), attribute(b));
  }

  static comparatorDescEmptyLast<S, T extends number | string>(attribute: (a: S) => Nullable<T>): (a: S, b: S) => number {
    return (a, b) => SortHelper.compareDescEmptyLast(attribute(a), attribute(b));
  }

  static comparatorAscEmptyFirst<S, T extends number | string>(attribute: (a: S) => Nullable<T>): (a: S, b: S) => number {
    return (a, b) => SortHelper.compareAscEmptyFirst(attribute(a), attribute(b));
  }

  static comparatorAscEmptyLast<S, T extends number | string>(attribute: (a: S) => Nullable<T>): (a: S, b: S) => number {
    return (a, b) => SortHelper.compareAscEmptyLast(attribute(a), attribute(b));
  }

  static comparatorAsc<S, T extends number | string>(attribute: (a: S) => T): (a: S, b: S) => number {
    return SortHelper.comparatorAscEmptyFirst(attribute);
  }

  static compareDescEmptyFirst<T extends number | string>(a: Nullable<T>, b: Nullable<T>): number {
    if (a === b || ((a === null || a === undefined) && (b === null || b === undefined))) {
      return 0;
    } else {
      if (a === null || a === undefined) {
        return -1;
      }
      if (b === null || b === undefined) {
        return 1;
      }
      return a < b ? 1 : -1;
    }
  }

  static compareAscEmptyLast<T extends number | string>(a: Nullable<T>, b: Nullable<T>): number {
    return SortHelper.compareDescEmptyFirst(b, a);
  }

  static compareDescEmptyLast<T extends number | string>(a: Nullable<T>, b: Nullable<T>): number {
    return SortHelper.compareAscEmptyFirst(b, a);
  }

  static sortByClusterComparator<T>(clusterPredicate: (item: T) => number): (a: T, b: T) => number {
    return (a: T, b: T) => {
      const clusterA = clusterPredicate(a);
      const clusterB = clusterPredicate(b);
      if (clusterA === clusterB) {
        return 0;
      }
      return clusterA < clusterB ? -1 : 1;
    };
  }

  /** arbitrary but total ordering, e.g. for CompareHelper.equalsArrayIgnoreOrder */
  static compareJson(a: any, b: any): number {
    const aJson = JSON.stringify(a);
    const bJson = JSON.stringify(b);
    return this.compareAscEmptyFirst(aJson, bJson);
  }

  /**
   * Chains given compare functions so that the first that returns a non-zero result is used.
   */
  static chain<T>(...compareFn: ((a: T, b: T) => number)[]): (a: T, b: T) => number {
    return (a: T, b: T) => {
      for (const c of compareFn) {
        const result = c(a, b);
        if (result !== 0) {
          return result;
        }
      }
      return 0;
    };
  }

  static localCompare(a: Nullable<string>, b: Nullable<string>, lowerCase = false): number {
    if (a && b) {
      return (lowerCase ? a.toLocaleLowerCase() : a).localeCompare(lowerCase ? b.toLocaleLowerCase() : b);
    } else {
      if (!a && b) {
        return 1;
      } else if (a && !b) {
        return -1;
      } else {
        return 0;
      }
    }
  }
}
